import {useEffect, useState} from 'react';
import {miniApp, viewport, backButton, themeParams, retrieveLaunchParams} from '@tma.js/sdk-react';

/**
 * Hook to get Telegram user data
 */
export function useTelegramUser() {
    const [user, setUser] = useState(null);

    useEffect(() => {
        try {
            const launchParams = retrieveLaunchParams();

            if (launchParams.tgWebAppData?.user) {
                setUser(launchParams.tgWebAppData.user);
            }
        } catch (err) {
            // Silently fail
        }
    }, []);

    return user;
}

/**
 * Hook to get Telegram init data
 */
export function useTelegramInitData() {
    const [rawInitData, setRawInitData] = useState('');

    useEffect(() => {
        try {
            const launchParams = retrieveLaunchParams();

            // Try to find initDataRaw in various possible locations
            const possibleRaw = launchParams.initDataRaw ||
                launchParams.tgWebAppData?.raw ||
                window.Telegram?.WebApp?.initData;

            if (possibleRaw) {
                setRawInitData(possibleRaw);
            }
        } catch (err) {
            // Silently fail
        }
    }, []);

    return rawInitData;
}

/**
 * Hook to get Telegram theme params as object
 */
export function useTelegramTheme() {
    const [theme, setTheme] = useState({
        backgroundColor: '#17212b',
        textColor: '#f5f5f5',
        hintColor: '#708499',
        linkColor: '#6ab3f3',
        buttonColor: '#5288c1',
        buttonTextColor: '#ffffff',
        secondaryBackgroundColor: '#232e3c',
        sectionBackgroundColor: '#17212b',
        sectionHeaderTextColor: '#6ab3f3',
        sectionSeparatorColor: '#111921',
    });
    const colors = {
        "accent_text_color": "#6ab2f2",
        "bg_color": "#17212b",
        "bottom_bar_bg_color": "#17212b",
        "button_color": "#5288c1",
        "button_text_color": "#ffffff",
        "destructive_text_color": "#ec3942",
        "header_bg_color": "#17212b",
        "hint_color": "#708499",
        "link_color": "#6ab3f3",
        "secondary_bg_color": "#232e3c",
        "section_bg_color": "#17212b",
        "section_header_text_color": "#6ab3f3",
        "section_separator_color": "#111921",
        "subtitle_text_color": "#708499",
        "text_color": "#f5f5f5"
    }

    useEffect(() => {
        try {
            const updateTheme = () => {
                setTheme({
                    backgroundColor: themeParams.bg_color?.() || '#ffffff',
                    textColor: themeParams.text_color?.() || '#000000',
                    hintColor: themeParams.hint_color?.() || '#999999',
                    linkColor: themeParams.link_color?.() || '#3390ec',
                    buttonColor: themeParams.button_color?.() || '#3390ec',
                    buttonTextColor: themeParams.button_text_color?.() || '#ffffff',
                    secondaryBackgroundColor: themeParams.secondary_bg_color?.() || '#f4f4f5',
                    sectionBackgroundColor: themeParams.section_bg_color?.() || '#ffffff',
                    sectionHeaderTextColor: themeParams.section_header_text_color?.() || '#8e8e93',
                    sectionSeparatorColor: themeParams.section_separator_color?.() || '#c8c7cc',
                });
            };

            updateTheme();

            // Subscribe to theme changes if available
            const unsubscribes = [];
            if (themeParams.backgroundColor?.sub) {
                unsubscribes.push(themeParams.backgroundColor.sub(updateTheme));
            }
            if (themeParams.textColor?.sub) {
                unsubscribes.push(themeParams.textColor.sub(updateTheme));
            }

            return () => {
                unsubscribes.forEach(unsub => unsub?.());
            };
        } catch (err) {
            console.warn('Failed to get theme params:', err);
        }
    }, []);

    return theme;
}

/**
 * Hook to manage Telegram back button
 */
export function useTelegramBackButton() {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        try {
            // Get current value
            const currentVisible = backButton.isVisible?.();
            if (typeof currentVisible === 'boolean') {
                setIsVisible(currentVisible);
            }

            // Subscribe to changes if available
            if (backButton.isVisible?.sub) {
                const unsubscribe = backButton.isVisible.sub((visible) => {
                    setIsVisible(visible);
                });
                return unsubscribe;
            }
        } catch (err) {
            console.warn('Failed to subscribe to back button:', err);
        }
    }, []);

    return {
        show: () => {
            try {
                backButton.show?.();
            } catch (err) {
                console.warn('Failed to show back button:', err);
            }
        },
        hide: () => {
            try {
                backButton.hide?.();
            } catch (err) {
                console.warn('Failed to hide back button:', err);
            }
        },
        onClick: (handler) => {
            try {
                backButton.on?.('click', handler);
            } catch (err) {
                console.warn('Failed to add back button listener:', err);
            }
        },
        offClick: (handler) => {
            try {
                backButton.off?.('click', handler);
            } catch (err) {
                console.warn('Failed to remove back button listener:', err);
            }
        },
        isVisible,
    };
}

/**
 * Hook to get Safe Area Insets
 */
export function useSafeAreaInsets() {
    const [insets, setInsets] = useState({
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    });

    useEffect(() => {
        try {
            const updateInsets = () => {
                setInsets({
                    top: viewport.safeAreaInsetTop?.() || 0,
                    bottom: viewport.safeAreaInsetBottom?.() || 0,
                    left: viewport.safeAreaInsetLeft?.() || 0,
                    right: viewport.safeAreaInsetRight?.() || 0,
                });
            };

            updateInsets();

            // Subscribe to changes if available
            const unsubscribes = [];
            if (viewport.safeAreaInsetTop?.sub) {
                unsubscribes.push(viewport.safeAreaInsetTop.sub(updateInsets));
            }

            return () => {
                unsubscribes.forEach(unsub => unsub?.());
            };
        } catch (err) {
            console.warn('Failed to get safe area insets:', err);
        }
    }, []);

    return insets;
}

/**
 * Hook to get Content Safe Area Insets
 */
export function useContentSafeAreaInsets() {
    const [insets, setInsets] = useState({
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    });

    useEffect(() => {
        try {
            const updateInsets = () => {
                setInsets({
                    top: viewport.contentSafeAreaInsetTop?.() || 0,
                    bottom: viewport.contentSafeAreaInsetBottom?.() || 0,
                    left: viewport.contentSafeAreaInsetLeft?.() || 0,
                    right: viewport.contentSafeAreaInsetRight?.() || 0,
                });
            };

            updateInsets();

            // Subscribe to changes if available
            const unsubscribes = [];
            if (viewport.contentSafeAreaInsetTop?.sub) {
                unsubscribes.push(viewport.contentSafeAreaInsetTop.sub(updateInsets));
            }

            return () => {
                unsubscribes.forEach(unsub => unsub?.());
            };
        } catch (err) {
            console.warn('Failed to get content safe area insets:', err);
        }
    }, []);

    return insets;
}

// Re-export useful components
export {miniApp, viewport, backButton, themeParams, retrieveLaunchParams};
